/**
 * Sesiones de worklist clínico en IndexedDB (worklist_sessions).
 * Contrato: docs/CONTRATO-VIEWER-WORKLIST.md
 */
import {
  dicomCacheDb as db,
  DICOM_OFFLINE_CACHE_ENABLED,
  isDbAvailable,
  openDicomCacheDb,
} from './dicomCache'

/** Máximo de sesiones retenidas. */
export const WORKLIST_SESSION_MAX = 30
/** Edad máxima en ms (14 días). */
export const WORKLIST_SESSION_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

async function ready() {
  if (!DICOM_OFFLINE_CACHE_ENABLED) return false
  if (!isDbAvailable()) return false
  try {
    if (!db.isOpen()) {
      await Promise.race([
        openDicomCacheDb(),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('[worklistSessions] IDB open timeout (5 s)')),
            5000
          )
        ),
      ])
    }
    return true
  } catch (e) {
    console.warn('[worklistSessions] db open', e)
    return false
  }
}

/**
 * @param {object} item
 * @returns {object|null}
 */
function snapshotItem(item) {
  if (!item || typeof item !== 'object') return null
  const ticketId =
    item.ticketId != null
      ? String(item.ticketId).trim()
      : item.ticket_id != null
        ? String(item.ticket_id).trim()
        : ''
  if (!ticketId) return null
  return {
    ticketId,
    studyInstanceUid:
      item.studyInstanceUid || item.study_instance_uid || null,
    studyDate: item.studyDate || item.study_date || null,
    studyTime: item.studyTime || item.study_time || null,
    modality: item.modality || null,
    description: item.description || null,
    patientId: item.patientId || item.patient_id || null,
    patientName: item.patientName || item.patient_name || null,
    order: item.order != null ? Number(item.order) : null,
    isCurrent: Boolean(item.isCurrent || item.is_current),
    clinicName: item.clinicName || item.clinic_name || null,
    seriesCount: item.seriesCount != null ? item.seriesCount : null,
    instanceCount: item.instanceCount != null ? item.instanceCount : null,
  }
}

/**
 * @param {{
 *   worklistId: string,
 *   title?: string|null,
 *   expiresAt?: string|null,
 *   ticketApi?: string|null,
 *   items?: object[],
 *   receivedAt?: number,
 * }} input
 * @returns {Promise<object|null>}
 */
export async function upsertWorklistSession(input) {
  if (!(await ready())) return null
  const worklistId =
    input && input.worklistId != null ? String(input.worklistId).trim() : ''
  if (!worklistId) return null
  const receivedAt =
    input.receivedAt != null && Number.isFinite(Number(input.receivedAt))
      ? Number(input.receivedAt)
      : Date.now()
  const items = Array.isArray(input.items)
    ? input.items.map(snapshotItem).filter(Boolean)
    : []
  const row = {
    worklistId,
    title: input.title != null && String(input.title).trim() !== ''
      ? String(input.title).trim()
      : null,
    expiresAt: input.expiresAt || null,
    ticketApi: input.ticketApi || null,
    itemCount: items.length,
    items,
    receivedAt,
  }
  try {
    await db.table('worklist_sessions').put(row)
    await pruneWorklistSessions()
    return row
  } catch (e) {
    console.warn('[worklistSessions] upsert', e)
    return null
  }
}

/**
 * @returns {Promise<object[]>}
 */
export async function listWorklistSessions() {
  if (!(await ready())) return []
  try {
    const rows = await db
      .table('worklist_sessions')
      .orderBy('receivedAt')
      .reverse()
      .toArray()
    return Array.isArray(rows) ? rows : []
  } catch (e) {
    console.warn('[worklistSessions] list', e)
    return []
  }
}

/**
 * @param {string} worklistId
 * @returns {Promise<object|null>}
 */
export async function getWorklistSession(worklistId) {
  const id = worklistId != null ? String(worklistId).trim() : ''
  if (!id || !(await ready())) return null
  try {
    const row = await db.table('worklist_sessions').get(id)
    return row || null
  } catch (e) {
    console.warn('[worklistSessions] get', e)
    return null
  }
}

/**
 * Retención: máx. WORKLIST_SESSION_MAX o más viejos que 14 días.
 * @returns {Promise<number>} cantidad eliminada
 */
export async function pruneWorklistSessions() {
  if (!(await ready())) return 0
  try {
    const rows = await db
      .table('worklist_sessions')
      .orderBy('receivedAt')
      .reverse()
      .toArray()
    const now = Date.now()
    const toDelete = []
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]
      if (!r || !r.worklistId) continue
      const age = now - Number(r.receivedAt || 0)
      if (i >= WORKLIST_SESSION_MAX || age > WORKLIST_SESSION_MAX_AGE_MS) {
        toDelete.push(r.worklistId)
      }
    }
    for (let j = 0; j < toDelete.length; j++) {
      await db.table('worklist_sessions').delete(toDelete[j])
    }
    return toDelete.length
  } catch (e) {
    console.warn('[worklistSessions] prune', e)
    return 0
  }
}

/**
 * Agrupa sesiones por etiqueta de día (Hoy / Ayer / fecha).
 * @param {object[]} sessions
 * @param {{ now?: number, locale?: string }} [opts]
 * @returns {{ label: string, sessions: object[] }[]}
 */
export function groupWorklistSessionsByDay(sessions, opts = {}) {
  const list = Array.isArray(sessions) ? sessions : []
  const now = opts.now != null ? opts.now : Date.now()
  const locale = opts.locale || 'es-AR'
  const startToday = new Date(now)
  startToday.setHours(0, 0, 0, 0)
  const startYesterday = new Date(startToday)
  startYesterday.setDate(startYesterday.getDate() - 1)

  /** @type {Map<string, object[]>} */
  const map = new Map()
  const order = []

  for (let i = 0; i < list.length; i++) {
    const s = list[i]
    if (!s) continue
    const ts = Number(s.receivedAt || 0)
    let label
    if (ts >= startToday.getTime()) {
      label = 'Hoy'
    } else if (ts >= startYesterday.getTime()) {
      label = 'Ayer'
    } else {
      try {
        label = new Date(ts).toLocaleDateString(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch (e) {
        label = String(ts)
      }
    }
    if (!map.has(label)) {
      map.set(label, [])
      order.push(label)
    }
    map.get(label).push(s)
  }

  return order.map((label) => ({
    label,
    sessions: map.get(label) || [],
  }))
}

/**
 * Título de UI para una sesión.
 * @param {object} session
 * @returns {string}
 */
export function formatWorklistSessionTitle(session) {
  if (!session) return 'Worklist'
  if (session.title && String(session.title).trim()) {
    return String(session.title).trim()
  }
  const ts = Number(session.receivedAt || Date.now())
  let time = ''
  try {
    time = new Date(ts).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    time = String(ts)
  }
  const n = session.itemCount != null ? session.itemCount : (session.items || []).length
  return `Worklist · ${time}${n ? ` · ${n} estudios` : ''}`
}
